<template>
    <div class="flex flex-col flex-1">
        <div class="form-card flex flex-col">
            <div :class="{'hide': !showForm}" class="flex flex-col items-center w-full">
                <div class="form-content">
                    <Alert color="cyan" v-if="form.two_way_enabled && !authContext.booking_reference" class="w-full mb-4">
                        You dont have active reservation, you can't submit this form
                    </Alert>
                    <div
                        v-for="(step, i) in schema"
                        :key="i"
                        :class="[{ current: i === currentStep }, 'step']"
                    >
                        <FormulateForm
                            class="w-full"
                            :schema="step"
                            :name="`step${i}`"
                            :errors="formErrors"
                            @submit="merge"
							@input="input"
                        ></FormulateForm>
                    </div>

                    <div
                        class="max-w-md w-full grid gap-4 grid-rows-1 grid-flow-col mt-4"
                    >
                        <button
                            v-if="schema.length > 1 && currentStep > 0"
                            @click.prevent="prev()"
                            class="flex justify-center items-center bg-white border-2 border-gray-500 p-2 text-gray-500 rounded-sm focus:outline-none"
                        >
                            <svg
                                class="w-6"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                                />
                            </svg>
                            <span class="ml-3">{{ $t("form.prev") }}</span>
                        </button>
                        <button
                            v-if="
                                schema.length > 1 &&
                                    currentStep + 1 < schema.length
                            "
                            @click.prevent="next()"
                            class="flex justify-center items-center bg-green-500 p-2 text-white rounded-sm focus:outline-none bb-form-button"
                        >
                            <span class="mr-3">{{ $t("form.next") }}</span>
                            <svg
                                class="w-6"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                                />
                            </svg>
                        </button>
                        <button
                            v-if="currentStep + 1 === schema.length"
                            @click.prevent="submit()"
                            :disabled="disabled || (form.two_way_enabled && !authContext.booking_reference)"
                            class="max-w-xs flex justify-center items-center bg-green-500 p-2 text-white rounded-sm focus:outline-none bb-form-button"
                            :class="{'opacity-75': (disabled || (form.two_way_enabled && !authContext.booking_reference))}"
                        >
                            {{ $t("form.submit") }}
                        </button>
                    </div>
                </div>
            </div>
			<!-- shows when loader displayed -->
			<Loader v-if="showLoader" ref="loader">{{form.loader_message}}</Loader>
			<!-- shows when submission success -->
			<Alert color="green" v-if="showResponse && formResponse && formResponse.message" class="w-full">{{formResponse.message}}</Alert>
            <!-- shown when something goes wrong, and eventbus returned error -->
            <Alert color="red" v-if="showResponse && formResponse && formResponse.error" class="w-full">{{formResponse.error}}</Alert>

            <!-- shown when we need display button after form submission -->
			<!-- todo: later add this button inside of message maybe -->
			<div class="flex flex-col" v-if="showResponse && formResponse && formResponse.button_url">
				<button
					class="max-w-xs flex justify-center items-center bg-green-500 p-2 text-white rounded-sm focus:outline-none bb-form-button"
					@click="window.open(formResponse.button_url)"
				>
					{{formResponse.button_text}}
				</button>
			</div>
        </div>
    </div>
</template>

<script>
import { mergeDeep } from "@/core/utils";
import {mapGetters, mapMutations} from "vuex";
import Loader from "@/components/common/Loader";
import Alert from "@/components/common/Alert";

export default {
    name: "Form",
    components: { Alert, Loader },
    data() {
        return {
            data: {},
            currentStep: 0,
            disabled: false,
        };
    },
    computed: {
        ...mapGetters({
            form: "form/form",
            state: "form/state",
            schema: "form/schema",
            formErrors: "form/formErrors",
            formResponse: "form/formResponse",
            authContext: "auth/context"
        }),
        showForm() {
            return ['form_rendered', 'user_error', 'server_timeout'].includes(this.state);
        },
        showLoader() {
            return ['form_submitted', 'async_waiting_response'].includes(this.state);
        },
        showResponse() {
            return ['async_response_received', 'async_response_timeout', 'response_received'].includes(this.state);
        },
        currentForm() {
            return this.$formulate.registry.get("step" + this.currentStep);
        }
    },
    methods: {
        ...mapMutations({
            resetError: "form/resetError",
            setFormResponse: "form/setFormResponse",
            setRenderedViewType: "app/setRenderedViewType"
        }),
        next() {
            this.currentForm.formSubmitted();
            if (this.currentForm.isValid) {
                this.currentStep++;
            }
        },
        prev() {
            if (this.currentStep >= 1) {
                this.currentStep--;
            }
        },
        merge(data) {
            this.data = mergeDeep(this.data, data);
        },
        input(data) {
            for(const input in data) {
                if (this.currentForm.failingFields === undefined) {
                    continue;
                }

                if (this.currentForm.failingFields[input] === undefined) {
                    // this.resetError(input);
                }
            }
        },
        async submit() {
            if (!this.currentForm) return false;

            await this.currentForm.formSubmitted();

            /* submit allowed only when form rendered, or there is input error (validation), or timeout error by server */
            if (this.currentForm.isValid && this.showForm) {

                this.$store
                    .dispatch("form/submit", {
                        slug: this.$route.params.slug,
                        data: this.data
                    }).finally();
            }
        }
    },
    mounted() {
        this.setRenderedViewType("form");
        this.$store.dispatch("form/fetch", this.$route.params.slug);
    }
};
</script>
<style>
.formulate-input .formulate-input-element input,
.formulate-input .formulate-input-element select,
.formulate-input .formulate-input-element textarea {
	background-color: var(--form-input-background-color);
	color: var(--form-input-color);
	box-shadow: var(--form-input-box-shadow);
	border-style:  var(--form-input-border-style, solid);
	border-width: var(--form-input-border-width, 1px);
}

.bb-form-button {
	color: var(--form-button-color,rgba(255, 255, 255, var(--tw-text-opacity)));
	background: var(--form-button-background-color,rgba(34, 197, 94, var(--tw-bg-opacity)));
}

.hide {
    display: none;
}
.step {
    display: none;
}
.step.current {
    display: block;
}
[data-view-type="form"] .bb-view-background {
    background: var(--form-background);
}

.form-card {
    display: flex;
    width: 100%;
    padding: 1rem;
    border-radius: 0.5rem;
    background: white;
}
.form-content {
    /*input max width*/
    max-width: 320px;
    width: 100%;
}
</style>
